<template>
  <main class="l-main p-register set">
    <div class="l-block">
      <div class="p-register-nav">
        <Button :to="backTo" variant="light" icon="pageback" radius>
          {{ projectId ? 'サイト設定に戻る' : 'サイト一覧に戻る' }}
        </Button>
        <Button variant="light" class="logout" radius @click="logout">
          ログアウト
        </Button>
      </div>
      <TitleRegister />
      <div class="l-block__content">
        <div class="hTitle">
          <h1>GA4を連携します。</h1>
        </div>
        <Loading v-if="isLoading.ga4Accounts" transparent />
        <div v-else-if="errorGa4Accounts">
          <Alert class="l-mt-3">{{ errorGa4Accounts }}</Alert>
          <div class="btnArea">
            <div class="form-auto">
              <Button
                :to="backTo"
                variant="border"
                size="w260"
                fsize="f16"
                icon="return_gn"
              >
                戻る
              </Button>
            </div>
          </div>
        </div>
        <template v-else>
          <div class="flex">
            <div class="title">
              <p>アカウント</p>
            </div>
            <div class="form c-alertPos">
              <Select
                :options="ga4Accounts"
                :selected="params.ga4AccountId"
                :disabled="!ga4Accounts.length"
                required
                placeholder="アカウントを選択してください"
                :class="{
                  alert:
                    errorGa4Properties ||
                    (isEdited.ga4AccountId && error.ga4AccountId)
                }"
                @change="changeAccount"
              />
              <Alert v-if="errorGa4Properties" class="l-mt-2">
                {{ errorGa4Properties }}
              </Alert>
              <Alert
                v-else-if="isEdited.ga4AccountId && error.ga4AccountId"
                class="l-mt-2"
              >
                {{ error.ga4AccountId }}
              </Alert>
            </div>
          </div>
          <div class="flex">
            <div class="title">
              <p>プロパティ</p>
            </div>
            <div class="form c-alertPos">
              <Select
                :options="ga4Properties"
                :selected="params.ga4PropertyId"
                :disabled="!ga4Properties.length"
                required
                placeholder="アカウントを選択後、プロパティを選択してください"
                :class="{
                  alert:
                    errorGa4DataStreams ||
                    (isEdited.ga4PropertyId && error.ga4PropertyId)
                }"
                @change="changeProperty"
              />
              <Alert v-if="errorGa4DataStreams" class="l-mt-2">
                {{ errorGa4DataStreams }}
              </Alert>
              <Alert
                v-else-if="isEdited.ga4PropertyId && error.ga4PropertyId"
                class="l-mt-2"
              >
                {{ error.ga4PropertyId }}
              </Alert>
            </div>
          </div>
          <div class="flex">
            <div class="title">
              <p>データストリーム</p>
            </div>
            <div class="form c-alertPos">
              <Select
                :options="ga4DataStreams"
                :selected="params.ga4DataStreamId"
                :disabled="!ga4DataStreams.length"
                required
                placeholder="プロパティを選択後、データストリームを選択してください"
                @change="changeProfile"
                :class="{
                  alert: isEdited.ga4DataStreamId && error.ga4DataStreamId
                }"
              />
              <Alert
                v-if="isEdited.ga4DataStreamId && error.ga4DataStreamId"
                class="l-mt-2"
              >
                {{ error.ga4DataStreamId }}
              </Alert>
            </div>
          </div>
          <div class="btnArea">
            <div class="form-auto">
              <Button
                variant="main"
                size="w260"
                fsize="f16"
                icon="arrow_r"
                :class="{ 'is-loading': isProcessing }"
                :disabled="isError || isProcessing"
                @click="submit"
              >
                連携する
              </Button>
            </div>
          </div>
          <div v-if="errorResponse" class="l-mt-3">
            <Alert>{{ errorResponse }}</Alert>
          </div>
        </template>
      </div>
    </div>
  </main>
</template>

<script>
import { ERROR_MESSAGE } from '@/constants';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Loading from '@/components/Loading';
import Select from '@/components/Select';
import TitleRegister from '@/components/TitleRegister';
import apiProjects from '@/api/projects';
import googleGA4API from '@/auth/GoogleGA4API';
import utils from '@/utils';
import Validation from '@/validation';

export default {
  components: {
    Alert,
    Button,
    Loading,
    Select,
    TitleRegister
  },
  data() {
    return {
      data: {
        ga4Accounts: [],
        ga4Properties: [],
        ga4DataStreams: []
      },
      error: {
        ga4AccountId: '',
        ga4PropertyId: '',
        ga4DataStreamId: ''
      },
      errorResponse: '',
      googleGA4API,
      params: {
        ga4AccountId: '',
        ga4PropertyId: '',
        ga4DataStreamId: '',
        isGa4CrossDomain: false
      },
      projectId: '',
      isEdited: {
        ga4AccountId: false,
        ga4PropertyId: false,
        ga4DataStreamId: false
      },
      isError: false,
      isLoading: {
        ga4Accounts: true,
        ga4Properties: true,
        ga4DataStreams: true
      },
      isProcessing: false
    };
  },
  computed: {
    backTo() {
      if (this.projectId) {
        return {
          name: 'configProject',
          params: { projectId: this.projectId }
        };
      } else {
        return {
          name: 'projects'
        };
      }
    },
    errorGa4Accounts() {
      if (!this.isLoading.ga4Accounts && !this.data.ga4Accounts.length) {
        return ERROR_MESSAGE.ga4.no_account;
      }
      return '';
    },
    errorGa4Properties() {
      if (!this.isLoading.ga4Properties && !this.data.ga4Properties.length) {
        return ERROR_MESSAGE.ga4.no_property;
      }
      return '';
    },
    errorGa4DataStreams() {
      if (!this.isLoading.ga4DataStreams && !this.data.ga4DataStreams.length) {
        return ERROR_MESSAGE.ga4.no_data_stream;
      }
      return '';
    },
    ga4Accounts() {
      return this.getSelectOptions(this.data.ga4Accounts);
    },
    ga4Properties() {
      return this.getSelectOptions(this.data.ga4Properties);
    },
    ga4DataStreams() {
      return this.getSelectOptions(this.data.ga4DataStreams);
    },
    project() {
      return this.projects.find(project => {
        return project.id === this.projectId;
      });
    },
    projects() {
      return this.$store.state.user.data.projects;
    },
    tmpKey() {
      return this.$store.state.set.ga4.params.tmpKey;
    }
  },
  watch: {
    error: {
      handler() {
        this.checkError();
      },
      deep: true
    },
    'params.ga4AccountId': {
      handler(value, oldValue) {
        this.error.ga4AccountId = new Validation()
          .select()
          .test(this.params.ga4AccountId);
      },
      immediate: true
    },
    'params.ga4PropertyId': {
      handler(value, oldValue) {
        this.error.ga4PropertyId = new Validation()
          .select()
          .test(this.params.ga4PropertyId);
      },
      immediate: true
    },
    'params.ga4DataStreamId': {
      handler(value, oldValue) {
        this.error.ga4DataStreamId = new Validation()
          .select()
          .test(this.params.ga4DataStreamId);
      },
      immediate: true
    }
  },
  created() {
    // GA4連携画面に直接アクセスしてきた場合は、プロジェクト一覧画面にリダイレクトさせる
    // * 「GA4と連携」ボタンをクリックしていない場合
    // * Googleの認可を通っていない場合
    // * 対象プロジェクトが GET /v1/projects のレスポンスに含まれていない場合
    // * 対象プロジェクトがすでにGA4連携している場合
    if (!this.tmpKey) {
      this.goProjectsPage();
      return;
    }
    this.auth
      .getUserData()
      .then(response => {
        const projects = response.data.user_metadata.projects;
        let ga4AccessToken = '';
        for (let i = 0; i < projects.length; i++) {
          if (projects[i].tmpKey === this.tmpKey) {
            this.projectId = String(projects[i].projectId);
            ga4AccessToken = projects[i].ga4AccessToken;
            break;
          }
        }
        if (!this.projectId || !ga4AccessToken) {
          console.error('ga4-0001');
          this.goProjectsPage();
          return;
        }
        if (!this.project) {
          console.error('ga4-0002');
          this.goProjectsPage();
          return;
        }

        this.googleGA4API.setToken(ga4AccessToken);
        this.getGA4Accounts();
      })
      .catch(error => {
        console.error('ga4-0003', error);
        this.goErrorPage();
      });
  },
  methods: {
    checkError() {
      this.isError = utils.checkError(this.error);
    },
    changeAccount(value) {
      this.isEdited.ga4AccountId = true;
      this.params.ga4AccountId = value;
      this.resetGA4Properties();
      this.resetGA4DataStreams();
      this.getGA4Properties();
    },
    changeProperty(value) {
      this.isEdited.ga4PropertyId = true;
      this.params.ga4PropertyId = value;
      this.resetGA4DataStreams();
      this.getGA4DataStreams();
    },
    changeProfile(value) {
      this.isEdited.ga4DataStreamId = true;
      this.params.ga4DataStreamId = value;
    },
    getGA4Accounts() {
      this.googleGA4API
        .getGA4Accounts()
        .then(response => {
          if (response.data && response.data.accounts) {
            this.data.ga4Accounts = response.data.accounts;
          } else {
            this.data.ga4Accounts = [];
          }
          this.isLoading.ga4Accounts = false;
        })
        .catch(error => {
          console.error('ga4-0004', error);
          this.goErrorPage();
        });
    },
    getGA4Properties() {
      if (!this.params.ga4AccountId) {
        return;
      }
      const params = {
        filter: `parent:${this.params.ga4AccountId}`
      };
      this.googleGA4API
        .getGA4Properties(params)
        .then(response => {
          if (response.data && response.data.properties) {
            this.data.ga4Properties = response.data.properties;
          } else {
            this.data.ga4Properties = [];
          }
          this.isLoading.ga4Properties = false;
        })
        .catch(error => {
          console.error('ga4-0005', error);
          this.goErrorPage();
        });
    },
    getGA4DataStreams() {
      if (!this.params.ga4PropertyId) {
        return;
      }
      this.googleGA4API
        .getGA4DataStreams(this.params.ga4PropertyId)
        .then(response => {
          if (response.data && response.data.dataStreams) {
            this.data.ga4DataStreams = response.data.dataStreams;
          } else {
            this.data.ga4DataStreams = [];
          }
          this.isLoading.ga4DataStreams = false;
        })
        .catch(error => {
          console.error('ga4-0006', error);
          this.goErrorPage();
        });
    },
    getSelectOptions(array) {
      return array.map(item => {
        return {
          label: item.displayName,
          value: item.name
        };
      });
    },
    goErrorPage() {
      this.$router.push({ name: 'setErrorGA4' });
    },
    goProjectsPage() {
      this.$router.push({ name: 'projects' });
    },
    resetGA4Properties() {
      this.data.ga4Properties = [];
      this.isEdited.ga4PropertyId = false;
      this.isLoading.ga4Properties = true;
      this.params.ga4PropertyId = '';
    },
    resetGA4DataStreams() {
      this.data.ga4DataStreams = [];
      this.isEdited.ga4DataStreamId = false;
      this.isLoading.ga4DataStreams = true;
      this.params.ga4DataStreamId = '';
    },
    async submit() {
      if (this.isError || this.isProcessing) {
        return;
      }
      this.isProcessing = true;

      const params = {
        ga4AccountId: this.params.ga4AccountId,
        ga4PropertyId: this.params.ga4PropertyId,
        ga4DataStreamId: this.params.ga4DataStreamId,
        ga4TmpKey: this.tmpKey,
        isGa4CrossDomain: 0
      };

      // GA4の情報を送る
      try {
        await apiProjects.ga4.put(params, this.auth.idToken, this.projectId);
      } catch (error) {
        if (error.response && error.response.data) {
          this.errorResponse = error.response.data.errors[0].userMessage;
        }
        this.isProcessing = false;
        return;
      }

      // Auth0に一時的に保存したサイト情報を削除する
      try {
        const response = await this.auth.getUserData();
        const metaData = response.data.user_metadata;
        // tmpKeyが一致するサイトを削除する
        metaData.projects = metaData.projects.filter(item => {
          if (item.tmpKey !== this.tmpKey) {
            return true;
          }
        });
        const data = { user_metadata: metaData };
        await this.auth.updateUserData(data);
      } catch (error) {
        // 失敗してもゴミデータが残るだけなので特に何もしない
      } finally {
        this.$store.dispatch('set/resetGA4');

        this.$router.push({
          name: 'configProject',
          params: { projectId: this.projectId }
        });
      }
    },
    logout() {
      this.auth.logout();
    }
  }
};
</script>
